import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { playSound } from '../utils/sound';
import { queueOrder, syncQueue, getTotalQueued } from '../utils/offlineQueue';
import api from '../utils/api';
import { devLog } from '../utils/logger';
import { Clock } from 'lucide-react';
import {
  OrderTakerHeader,
  OrderTakerNotice,
  OfflineBanner,
  CartBanner,
  OrderTakerTabs,
  MenuItemCard,
  CartCheckoutPanel,
  OrdersList,
} from './order/OrderTakerParts';

const ORDER_STATUS_PRIORITY = { ready: 0, in_progress: 1, new: 2 };

export default function OrderTaker() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('menu');
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [waitTime, setWaitTime] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showCheckout, setShowCheckout] = useState(false);
  const [payMethod, setPayMethod] = useState('');
  const [cashTendered, setCashTendered] = useState('');
  const [paymentMethods, setPaymentMethods] = useState({ cash: true, card: true, square: false });
  const [squareReady, setSquareReady] = useState(false);
  const [squareError, setSquareError] = useState('');
  const [squarePosWaiting, setSquarePosWaiting] = useState(false);
  const squareCardInstance = useRef(null);
  const squarePosTimeout = useRef(null);
  // Per-item customization builder
  const [buildingItem, setBuildingItem] = useState(null); // menu item id being configured
  const [buildCustomizations, setBuildCustomizations] = useState([]);
  const [buildChoiceSelections, setBuildChoiceSelections] = useState({});
  const [buildAddonQty, setBuildAddonQty] = useState({});
  const [missingRequiredGroups, setMissingRequiredGroups] = useState([]);
  const [missingRequiredPulse, setMissingRequiredPulse] = useState(0);
  const [buildNotes, setBuildNotes] = useState('');
  const [buildQty, setBuildQty] = useState(1);
  // Sound toggle
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('wc_sound') !== 'off');
  // Offline queue
  const [offlineCount, setOfflineCount] = useState(getTotalQueued());
  const [syncing, setSyncing] = useState(false);
  const prevConnected = useRef(false);
  const fetchMenu = useCallback(async () => { try { const { data } = await api.get('/menu'); setMenu(data); } catch (err) { devLog.error('Failed to fetch menu:', err); } }, []);
  const fetchOrders = useCallback(async () => { try { const { data } = await api.get('/orders'); setOrders(data); } catch (err) { devLog.error('Failed to fetch orders:', err); } }, []);
  const fetchWaitTime = useCallback(async () => { try { const { data } = await api.get('/board/wait-time'); setWaitTime(data); } catch (err) { devLog.error('Wait time fetch failed:', err); } }, []);
  const fetchPaymentMethods = useCallback(async () => { try { const { data } = await api.get('/settings/payment-methods'); setPaymentMethods(data); } catch (err) { devLog.error('Payment methods fetch failed:', err); } }, []);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { fetchMenu(); fetchOrders(); }, [fetchMenu, fetchOrders]);
  useEffect(() => {
    fetchPaymentMethods();
    const refreshOnOnline = () => fetchPaymentMethods();
    window.addEventListener('online', refreshOnOnline);
    return () => window.removeEventListener('online', refreshOnOnline);
  }, [fetchPaymentMethods]);
  useEffect(() => { if (showCheckout) fetchPaymentMethods(); }, [showCheckout, fetchPaymentMethods]);
  useEffect(() => { const t = setInterval(fetchMenu, 5000); return () => clearInterval(t); }, [fetchMenu]);
  useEffect(() => { fetchWaitTime(); const i = setInterval(fetchWaitTime, 20000); return () => clearInterval(i); }, [fetchWaitTime]);

  useEffect(() => {
    const rw = orders.some(o => o.status === 'ready' && o.completed_at && (now - new Date(o.completed_at).getTime()) / 60000 > 2);
    if (rw) document.title = document.title.startsWith('!!') ? 'Wildcraft' : '!! ORDER READY !!';
    else document.title = 'Wildcraft';
  }, [now, orders]);

  const playSoundIfEnabled = useCallback((type) => { if (soundEnabled) playSound(type); }, [soundEnabled]);
  const toggleSound = () => { const next = !soundEnabled; setSoundEnabled(next); localStorage.setItem('wc_sound', next ? 'on' : 'off'); };

  const clearOrderForm = useCallback(() => {
    setCart([]); setCustomerName(''); setTableNumber(''); setPhoneNumber(''); setOrderNote(''); setShowCheckout(false); setPayMethod(''); setCashTendered('');
  }, []);

  const onWsMessage = useCallback((msg) => {
    if (msg.type === 'menu_update' || msg.type === 'item_86') fetchMenu();
    if (['new_order', 'order_status', 'order_cancelled', 'daily_reset'].includes(msg.type)) {
      fetchOrders(); api.get('/board/wait-time').then(r => setWaitTime(r.data)).catch(() => {});
    }
    if (msg.type === 'order_status' && msg.status === 'ready') {
      playSoundIfEnabled('order_ready'); setNotification(`Order #${msg.order?.order_number} READY!`); setTimeout(() => setNotification(null), 5000);
    }
    if (msg.type === 'daily_reset') { setCart([]); fetchMenu(); }
  }, [fetchMenu, fetchOrders, playSoundIfEnabled]);
  const wsConnected = useWebSocket(onWsMessage);

  // Auto-sync when connection restores
  useEffect(() => {
    if (wsConnected && !prevConnected.current && offlineCount > 0) {
      setSyncing(true);
      syncQueue(api).then(result => {
        if (result.synced > 0) {
          playSoundIfEnabled('sync_complete');
          setNotification(`Synced ${result.synced} offline items`);
          setTimeout(() => setNotification(null), 4000);
          fetchOrders();
        }
        setOfflineCount(getTotalQueued());
        setSyncing(false);
      });
    }
    prevConnected.current = wsConnected;
  }, [wsConnected, offlineCount, playSoundIfEnabled, fetchOrders]);

  // Per-item builder: start building a new item
  const startBuildItem = (item) => {
    if (buildingItem === item.id) { setBuildingItem(null); return; }
    setBuildingItem(item.id);
    setBuildCustomizations([]);
    setBuildChoiceSelections({});
    setBuildAddonQty({});
    setMissingRequiredGroups([]);
    setBuildNotes('');
    setBuildQty(1);
  };

  const selectChoice = (groupId, choiceId) => {
    setBuildChoiceSelections(prev => ({ ...prev, [groupId]: choiceId }));
    setMissingRequiredGroups(prev => prev.filter(id => id !== groupId));
  };
  const changeAddonQty = (groupId, choiceId, delta, maxQty = 10) => {
    const key = `${groupId}:${choiceId}`;
    setBuildAddonQty(prev => {
      const nextQty = Math.max(0, Math.min(maxQty, (prev[key] || 0) + delta));
      const next = { ...prev };
      if (nextQty === 0) delete next[key];
      else next[key] = nextQty;
      return next;
    });
  };

  const getSelectedOptions = (item) => {
    const selected = [];
    (item.choice_groups || []).forEach(group => {
      if (group.allow_quantity) {
        (group.choices || []).forEach(choice => {
          const qty = buildAddonQty[`${group.id}:${choice.id}`] || 0;
          if (qty > 0) selected.push({ group_id: group.id, group_name: group.name, choice_id: choice.id, choice_name: choice.name, qty, price_delta: choice.price_delta || 0 });
        });
      } else if (buildChoiceSelections[group.id]) {
        const choice = (group.choices || []).find(c => c.id === buildChoiceSelections[group.id]);
        if (choice) selected.push({ group_id: group.id, group_name: group.name, choice_id: choice.id, choice_name: choice.name, qty: 1, price_delta: choice.price_delta || 0 });
      }
    });
    return selected;
  };

  const getMissingRequiredGroups = (item) => (item.choice_groups || []).filter(group => group.required && !group.allow_quantity && !buildChoiceSelections[group.id]);

  const getOptionTotal = (item) => getSelectedOptions(item).reduce((sum, option) => sum + (option.price_delta || 0) * option.qty, 0);

  const toggleBuildCust = (ingredientId, ingredientName, type) => {
    setBuildCustomizations(prev => {
      const existing = prev.findIndex(x => x.ingredient_id === ingredientId);
      if (existing >= 0) {
        if (prev[existing].type === type) return prev.filter((_, i) => i !== existing);
        return prev.map((x, i) => i === existing ? { ...x, type } : x);
      }
      return [...prev, { ingredient_id: ingredientId, ingredient_name: ingredientName, type, upcharge: type === 'extra' ? 0.50 : 0 }];
    });
  };

  const setBuildUpcharge = (ingredientId, upcharge) => {
    setBuildCustomizations(prev => prev.map(x => x.ingredient_id === ingredientId ? { ...x, upcharge: parseFloat(upcharge) || 0 } : x));
  };

  const addBuiltItemToCart = (item) => {
    const missing = getMissingRequiredGroups(item);
    if (missing.length > 0) {
      setMissingRequiredGroups(missing.map(group => group.id));
      setMissingRequiredPulse(n => n + 1);
      return;
    }
    const selectedOptions = getSelectedOptions(item);
    const newItem = {
      cartId: `cart_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      menu_item_id: item.id, name: item.name, price: item.price, qty: buildQty,
      notes: buildNotes, customizations: [...buildCustomizations], selected_options: selectedOptions, recipe: item.recipe || []
    };
    setCart(prev => [...prev, newItem]);
    setBuildingItem(null);
    setBuildCustomizations([]);
    setBuildChoiceSelections({});
    setBuildAddonQty({});
    setMissingRequiredGroups([]);
    setBuildNotes('');
    setBuildQty(1);
    playSoundIfEnabled('default');
  };

  const updateCartQty = (idx, d) => setCart(prev => prev.map((c, i) => i === idx ? { ...c, qty: Math.max(0, c.qty + d) } : c).filter(c => c.qty > 0));
  const removeCartItem = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const getItemTotal = (item) => {
    const base = item.price * item.qty;
    const extras = (item.customizations || []).filter(c => c.type === 'extra').reduce((s, c) => s + (c.upcharge || 0), 0) * item.qty;
    const selected = (item.selected_options || []).reduce((s, o) => s + (o.price_delta || 0) * (o.qty || 1), 0) * item.qty;
    return base + extras + selected;
  };

  const cartTotal = cart.reduce((s, c) => s + getItemTotal(c), 0);
  const changeDue = payMethod === 'cash' && cashTendered ? Math.max(0, parseFloat(cashTendered) - cartTotal) : 0;

  const submitOrder = async () => {
    if (!customerName.trim() || cart.length === 0 || !payMethod) return;
    if (payMethod === 'square' && paymentMethods.square && !paymentMethods.square_pos_mode) {
      await processSquarePayment();
      return;
    }
    setSubmitting(true);
    const orderPayload = buildOrderPayload(payMethod);

    if (payMethod === 'square' && paymentMethods.square_pos_mode) {
      openSquarePosDeepLink(orderPayload);
      setSubmitting(false);
      return;
    }

    if (!wsConnected) {
      // Offline — queue locally
      const count = queueOrder(orderPayload);
      setOfflineCount(count);
      playSoundIfEnabled('offline_queued');
      setNotification(`Order queued offline (${count} pending)`);
      setTimeout(() => setNotification(null), 4000);
      clearOrderForm();
      setSubmitting(false);
      return;
    }

    try {
      await api.post('/orders', orderPayload);
      clearOrderForm();
      playSoundIfEnabled('default');
    } catch (e) {
      // Network error — queue offline
      if (!e.response) {
        const count = queueOrder(orderPayload);
        setOfflineCount(count);
        playSoundIfEnabled('offline_queued');
        setNotification(`Connection lost — order queued (${count} pending)`);
        setTimeout(() => setNotification(null), 4000);
        clearOrderForm();
      } else {
        alert(e.response?.data?.detail || 'Failed');
      }
    } finally { setSubmitting(false); }
  };

  const markServed = async (id) => { try { await api.put(`/orders/${id}/status`, { status: 'served' }); } catch (err) { devLog.error('Mark served failed:', err); } };
  const [notifying, setNotifying] = useState(null);
  const notifyCustomer = async (id) => {
    setNotifying(id);
    try {
      const { data } = await api.post(`/orders/${id}/notify`);
      setNotification(`SMS sent to ${data.to}`);
      setTimeout(() => setNotification(null), 4000);
      fetchOrders();
    } catch (e) {
      setNotification(null);
      alert(e.response?.data?.detail || 'Could not send SMS');
    } finally { setNotifying(null); }
  };
  const cancelOrder = async (id) => { try { await api.put(`/orders/${id}/cancel`); } catch (e) { alert(e.response?.data?.detail || 'Cannot cancel'); } };

  const getSquareCallbackUrl = useCallback(() => {
    if (paymentMethods.square_callback_url && paymentMethods.square_callback_url.startsWith('http')) return paymentMethods.square_callback_url;
    return `${window.location.origin}/?square_callback=1`;
  }, [paymentMethods.square_callback_url]);

  const postSquarePosOrder = useCallback(async (pending, txnId = '') => {
    try {
      const payload = { ...pending.orderPayload, square_pos_transaction_id: txnId };
      await api.post('/orders', payload);
      playSoundIfEnabled('default');
      setNotification(`✓ Paid $${(pending.amount_cents / 100).toFixed(2)} — sent to kitchen`);
      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      if (!err.response) {
        const count = queueOrder(pending.orderPayload);
        setOfflineCount(count);
        setNotification(`Paid — order queued offline (${count} pending)`);
        setTimeout(() => setNotification(null), 5000);
      } else {
        setNotification('Paid, but failed to send order. Re-enter manually.');
        setTimeout(() => setNotification(null), 6000);
      }
    } finally {
      try { localStorage.removeItem('wc_square_pos_pending'); } catch (err) { devLog.error('Square pending cleanup failed:', err); }
      setSquarePosWaiting(false);
      clearOrderForm();
      try { window.history.replaceState({}, '', window.location.pathname); } catch (err) { devLog.error('Square callback cleanup failed:', err); }
    }
  }, [clearOrderForm, playSoundIfEnabled]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('square_callback') !== '1') return;
    const errorCode = params.get('com.squareup.pos.ERROR_CODE') || params.get('error_code');
    const errorDesc = params.get('com.squareup.pos.ERROR_DESCRIPTION') || params.get('error_description');
    const txnId = params.get('com.squareup.pos.CLIENT_TRANSACTION_ID') || params.get('com.squareup.pos.SERVER_TRANSACTION_ID') || params.get('client_transaction_id') || params.get('transaction_id');
    let dataPayload = null;
    try { dataPayload = params.get('data') ? JSON.parse(params.get('data')) : null; } catch (err) { dataPayload = null; }
    const success = !errorCode && (txnId || dataPayload?.status === 'ok');
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem('wc_square_pos_pending') || 'null'); } catch (err) { pending = null; }
    if (!success || !pending) {
      setSquarePosWaiting(false);
      setNotification(errorDesc || errorCode || 'Square payment cancelled');
      setTimeout(() => setNotification(null), 4000);
      try { localStorage.removeItem('wc_square_pos_pending'); } catch (err) { devLog.error('Square pending cleanup failed:', err); }
      try { window.history.replaceState({}, '', window.location.pathname); } catch (err) { devLog.error('Square callback cleanup failed:', err); }
      return;
    }
    postSquarePosOrder(pending, txnId || dataPayload?.transaction_id || '');
  }, [postSquarePosOrder]);

  const initSquare = async () => {
    setSquareError('');
    try {
      if (!paymentMethods.square_application_id || !paymentMethods.square_location_id) {
        setSquareError('Square Application ID and Location ID are required.');
        return;
      }
      if (!window.Square) {
        const script = document.createElement('script');
        script.src = paymentMethods.square_environment === 'production' ? 'https://web.squarecdn.com/v1/square.js' : 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        await new Promise((resolve, reject) => { script.onload = resolve; script.onerror = reject; document.head.appendChild(script); });
      }
      const payments = window.Square.payments(paymentMethods.square_application_id, paymentMethods.square_location_id);
      const card = await payments.card();
      await card.attach('#card-container');
      squareCardInstance.current = card;
      setSquareReady(true);
    } catch (err) {
      devLog.error('Square init failed:', err);
      setSquareError('Failed to load card form. Check Square config.');
    }
  };

  const processSquarePayment = async () => {
    if (!squareCardInstance.current) return;
    setSubmitting(true);
    setSquareError('');
    try {
      const result = await squareCardInstance.current.tokenize();
      if (result.status !== 'OK') {
        setSquareError(result.errors?.[0]?.message || 'Card error');
        setSubmitting(false);
        return;
      }
      const orderPayload = buildOrderPayload('square');
      const orderRes = await api.post('/orders', orderPayload);
      await api.post('/payments/square', { source_id: result.token, amount_cents: Math.round(cartTotal * 100), order_id: orderRes.data.id, currency: 'USD' });
      clearOrderForm();
      setSquareReady(false);
      squareCardInstance.current = null;
      playSoundIfEnabled('default');
    } catch (e) {
      setSquareError(e.response?.data?.detail || 'Payment failed');
    } finally { setSubmitting(false); }
  };

  const buildOrderPayload = (method = payMethod) => ({
    customer_name: customerName.trim(), seating: 'table', table_number: tableNumber, phone_number: phoneNumber,
    items: cart.map(c => ({
      menu_item_id: c.menu_item_id, qty: c.qty, notes: c.notes,
      customizations: (c.customizations || []).map(x => ({ ingredient_id: x.ingredient_id, ingredient_name: x.ingredient_name, type: x.type, upcharge: x.upcharge || 0 })),
      selected_options: (c.selected_options || []).map(x => ({ group_id: x.group_id, choice_id: x.choice_id, qty: x.qty || 1 }))
    })),
    special_instructions: orderNote,
    payment_method: method,
    amount_tendered: method === 'cash' ? parseFloat(cashTendered) || 0 : cartTotal,
    change_due: method === 'cash' ? changeDue : 0
  });

  const openSquarePosDeepLink = (orderPayload) => {
    const amountCents = Math.round(cartTotal * 100);
    const clientTxnId = `wc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const pending = { clientTxnId, amount_cents: amountCents, orderPayload: { ...orderPayload, payment_method: 'square_pos', amount_tendered: cartTotal, change_due: 0 } };
    try { localStorage.setItem('wc_square_pos_pending', JSON.stringify(pending)); } catch (err) { devLog.error('Square pending stash failed:', err); }
    if (paymentMethods.square_pos_demo_mode) {
      setSquarePosWaiting(true);
      if (squarePosTimeout.current) clearTimeout(squarePosTimeout.current);
      squarePosTimeout.current = setTimeout(() => postSquarePosOrder(pending, `demo_${clientTxnId}`), 2000);
      return;
    }
    if (!paymentMethods.square_application_id) {
      setSquareError('Square Application ID not configured');
      setSquarePosWaiting(false);
      return;
    }
    const data = {
      amount_money: { amount: amountCents, currency_code: 'USD' },
      callback_url: getSquareCallbackUrl(),
      client_id: paymentMethods.square_application_id,
      version: '1.3',
      notes: `Wildcraft Order — ${orderPayload.customer_name}${tableNumber ? ' · T' + tableNumber : ''} — ${clientTxnId}`,
      options: { supported_tender_types: ['CREDIT_CARD'] },
      state: clientTxnId,
    };
    setSquarePosWaiting(true);
    const before = Date.now();
    window.location.href = `square-commerce-v1://payment/create?data=${encodeURIComponent(JSON.stringify(data))}`;
    if (squarePosTimeout.current) clearTimeout(squarePosTimeout.current);
    squarePosTimeout.current = setTimeout(() => {
      if (!document.hidden && Date.now() - before >= 2400) {
        setSquarePosWaiting(false);
        setSquareError('Square POS app not detected — falling back to web card form.');
        try { localStorage.removeItem('wc_square_pos_pending'); } catch (err) { devLog.error('Square pending cleanup failed:', err); }
        initSquare();
      }
    }, 2500);
  };

  const statusClr = (s) => ({ new: 'bg-blue-100 text-blue-900', in_progress: 'bg-[#B8860B] text-white', ready: 'wc-success', served: 'bg-[#F0EDE5] text-[#7D8B69]' }[s] || 'bg-[#F0EDE5]');
  const readyCount = useMemo(() => orders.filter(o => o.status === 'ready').length, [orders]);
  const active = useMemo(() => orders
    .filter(o => !['served', 'cancelled'].includes(o.status))
    .sort((a, b) => {
      const priorityDiff = (ORDER_STATUS_PRIORITY[a.status] ?? 9) - (ORDER_STATUS_PRIORITY[b.status] ?? 9);
      if (priorityDiff !== 0) return priorityDiff;
      if (a.status === 'ready' && b.status === 'ready') {
        return new Date(a.completed_at || a.created_at).getTime() - new Date(b.completed_at || b.created_at).getTime();
      }
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }), [orders]);
  const itemCount = useMemo(() => cart.reduce((s, c) => s + c.qty, 0), [cart]);
  const fmtTime = (m) => `${Math.floor(m)}:${Math.floor((m % 1) * 60).toString().padStart(2, '0')}`;
  const isSuperAdmin = ['super_admin', 'admin'].includes(user.role);

  const buildTotal = (item) => ((item.price + getOptionTotal(item)) * buildQty) +
    (buildCustomizations.filter(c => c.type === 'extra').reduce((s, c) => s + (c.upcharge || 0), 0) * buildQty);

  const buildState = {
    buildQty,
    buildCustomizations,
    buildChoiceSelections,
    buildAddonQty,
    missingRequiredGroups,
    missingRequiredPulse,
    buildNotes,
  };

  const actions = {
    startBuildItem,
    setBuildQty,
    setBuildNotes,
    toggleBuildCust,
    setBuildUpcharge,
    selectChoice,
    changeAddonQty,
    addBuiltItemToCart,
    buildTotal,
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col" data-testid="order-taker-view">
      <OrderTakerHeader isSuperAdmin={isSuperAdmin} user={user} logout={logout} toggleSound={toggleSound} soundEnabled={soundEnabled} wsConnected={wsConnected} />
      <OrderTakerNotice notification={notification} />
      <OfflineBanner wsConnected={wsConnected} offlineCount={offlineCount} syncing={syncing} />
      <CartBanner cartLength={cart.length} tab={tab} showCheckout={showCheckout} itemCount={itemCount} cartTotal={cartTotal} />
      <OrderTakerTabs tab={tab} setTab={setTab} readyCount={readyCount} />

      <div className="flex-1 overflow-auto">
        {tab === 'menu' ? (
          <div className="space-y-4 px-4 py-4 sm:px-6">
            {waitTime && waitTime.active_orders > 0 && (
              <button onClick={() => setTab('orders')} className="min-h-14 w-full rounded-2xl border border-[#D9B66F] bg-[#FFF8E1] px-4 py-3 text-left shadow-sm transition-all duration-200 hover:bg-[#FFF4C7] active:scale-[0.99] sm:px-5" data-testid="wait-time-banner">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2"><Clock size={15} className="text-[#8A6500]" /><span className="font-mono text-sm font-bold uppercase tracking-wider text-[#1B3A1A]">{waitTime.active_orders} in queue</span></div>
                  <span className="font-mono text-sm font-bold text-[#8A6500]">~{waitTime.estimated_wait_minutes} min &rarr;</span>
                </div>
              </button>
            )}
            {menu.map(item => (
              <MenuItemCard key={item.id} item={item} cart={cart} buildingItem={buildingItem} buildState={buildState} actions={actions} />
            ))}
            {menu.length === 0 && <p className="rounded-3xl border border-dashed border-[#7D8B69]/25 bg-white py-12 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69] shadow-sm" data-testid="order-menu-empty">No menu items yet</p>}
            <CartCheckoutPanel
              cart={cart}
              itemCount={itemCount}
              cartTotal={cartTotal}
              getItemTotal={getItemTotal}
              updateCartQty={updateCartQty}
              removeCartItem={removeCartItem}
              customerState={{ customerName, setCustomerName, tableNumber, setTableNumber, phoneNumber, setPhoneNumber, orderNote, setOrderNote }}
              paymentState={{ showCheckout, setShowCheckout, payMethod, setPayMethod, cashTendered, setCashTendered, changeDue, submitting, paymentMethods, squareReady, squareError, squarePosWaiting }}
              squareActions={{ initSquare, processSquarePayment, launchSquarePos: () => openSquarePosDeepLink(buildOrderPayload('square_pos')), cancelSquarePos: () => { setSquarePosWaiting(false); if (squarePosTimeout.current) clearTimeout(squarePosTimeout.current); try { localStorage.removeItem('wc_square_pos_pending'); } catch (err) { devLog.error('Square pending cleanup failed:', err); } } }}
              submitOrder={submitOrder}
            />
          </div>
        ) : (
          <OrdersList active={active} now={now} fmtTime={fmtTime} statusClr={statusClr} notifying={notifying} notifyCustomer={notifyCustomer} markServed={markServed} cancelOrder={cancelOrder} />
        )}
      </div>
    </div>
  );
}

